import styled from '@emotion/styled'
import Link from 'next/link'
import React from 'react'

import Button from 'components/Button'
import ConfirmationModal from 'components/ConfirmationModal'
import UseAsyncFn from 'components/UseAsyncFn'
import { useIntegration } from 'hooks/useIntegration'
import { useTemplate } from 'hooks/useTemplate'
import useTemplateId from 'hooks/useTemplateId'
import { useAppDispatch } from 'lib/store'

const ButtonLink = styled(Link)`
	text-decoration: none;
`

type Props = {
	name: string
	caption: string
	handleDisconnect: () => Promise<void>
}

function DisconnectChatIntegrationModal({ name, caption, handleDisconnect }: Props) {
	const dispatch = useAppDispatch()
	const { template } = useTemplate()
	const { withTemplateId } = useTemplateId()
	const { integration: livechatIntegration } = useIntegration('livechat')
	const { integration: chatbotIntegration } = useIntegration('chatbot')
	const { integration: openaiIntegration } = useIntegration('openai')

	const isTheLastEnabledChatIntegration = React.useMemo(() => {
		const enabledIntegrations = [livechatIntegration, chatbotIntegration, openaiIntegration].filter(
			(integration) => integration?.connected,
		)
		return enabledIntegrations.length === 1
	}, [livechatIntegration, chatbotIntegration, openaiIntegration])

	function hideModal() {
		dispatch({ type: 'currentModal/set', payload: null })
	}

	if (isTheLastEnabledChatIntegration && template?.isChatOnlyModeEnabled) {
		return (
			<ConfirmationModal
				hideModal={hideModal}
				name="ConfirmDisableModal"
				data-testid="ChatOnlyGuardModal"
				title={`Chat-Only mode must be disabled to disconnect ${name}`}
				caption={`${name} can't be disconnected while Chat-Only mode is active. Update your visibility settings first.`}
			>
				<Button kind="secondary" type="button" disableHover onClick={hideModal}>
					Cancel
				</Button>
				<ButtonLink href={withTemplateId('/configurator/customize-look/advanced')}>
					<Button kind="primary" type="button" disableHover onClick={hideModal}>
						Open settings
					</Button>
				</ButtonLink>
			</ConfirmationModal>
		)
	}

	return (
		<ConfirmationModal
			hideModal={hideModal}
			name="ConfirmDisableModal"
			data-testid="ConfirmDisableModal"
			title={`Are you sure you want to disconnect ${name}?`}
			caption={caption}
		>
			<Button kind="secondary" type="button" disableHover onClick={hideModal}>
				Keep it
			</Button>
			<UseAsyncFn key="disconnect" asyncFn={handleDisconnect}>
				{({ loading, callAsyncFn }) => (
					<Button disableHover type="button" kind="destructive" loading={loading} onClick={callAsyncFn}>
						Disconnect
					</Button>
				)}
			</UseAsyncFn>
		</ConfirmationModal>
	)
}

export default DisconnectChatIntegrationModal
